import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import socketService from '../services/socket';
import '../styles/RoomBrowser.css';

interface Room {
  id: number;
  room_code: string;
  dictionary_name: string;
  max_players: number;
  status: string;
  round_number: number;
  participant_count: number;
}

interface RoomBrowserProps {
  onJoinSuccess?: () => void;
}

const RoomBrowser: React.FC<RoomBrowserProps> = ({ onJoinSuccess }) => {
  const { user } = useAuth();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [requestSent, setRequestSent] = useState<number | null>(null);

  const fetchRooms = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.get('/api/room-browser/active-rooms');
      setRooms(data.rooms || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch rooms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();

    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchRooms, 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRequestJoin = async (roomId: number, roomCode: string) => {
    if (!user) return;

    try {
      setError('');
      const response = await apiService.post('/api/room-browser/request-join', { roomId });
      setRequestSent(roomId);

      // Emit socket event to notify room participants
      socketService.getSocket()?.emit('join_request_sent', {
        roomId,
        requestId: response.request.id,
        requesterUserId: user.id,
        requesterUsername: user.username
      });

      // Clear the "request sent" message after 5 seconds
      setTimeout(() => setRequestSent(null), 5000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send join request');
    }
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'waiting':
        return '⏸️ In Lobby';
      case 'in_progress':
        return '🎮 Playing';
      case 'ready':
        return '⏳ Next Round';
      default:
        return status;
    }
  };

  return (
    <div className="room-browser">
      <div className="browser-header">
        <h3>🌐 Browse Active Rooms</h3>
        <button onClick={fetchRooms} className="refresh-btn" disabled={loading}>
          {loading ? '⟳ Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {requestSent && (
        <div className="success-banner">
          ✓ Join request sent! Waiting for room members to respond...
        </div>
      )}

      <div className="rooms-list">
        {rooms.length === 0 ? (
          <div className="no-rooms">
            {loading ? 'Loading rooms...' : 'No active rooms available'}
          </div>
        ) : (
          rooms.map((room, index) => {
            const isFull = room.participant_count >= room.max_players;
            return (
              <div key={room.id} className={`room-card ${isFull ? 'full' : ''}`}>
                <div className="room-header">
                  <h4 className="room-code">Game Room #{index + 1}</h4>
                  {isFull && <span className="full-badge">FULL</span>}
                </div>
                
                <div className="room-info">
                  <div className="info-row">
                    <span className="label">📖 Topic:</span>
                    <span className="value">{room.dictionary_name}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">👥 Players:</span>
                    <span className="value">
                      {room.participant_count}/{room.max_players === 100 ? '∞' : room.max_players}
                    </span>
                  </div>
                  <div className="info-row">
                    <span className="label">🔢 Round:</span>
                    <span className="value">{room.round_number}</span>
                  </div>
                  <div className="info-row">
                    <span className="label">📊 Status:</span>
                    <span className="value">{getStatusDisplay(room.status)}</span>
                  </div>
                </div>

                <button
                  className="join-request-btn"
                  onClick={() => handleRequestJoin(room.id, room.room_code)}
                  disabled={isFull || requestSent === room.id}
                >
                  {requestSent === room.id ? '✓ Request Sent' : isFull ? '🔒 Room Full' : '📩 Request to Join'}
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RoomBrowser;
